import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRecommendationsDashboard } from '/@/hooks/dashboards';
import type { useGetUserNode } from '/@/hooks/nodes';
import { useCurrentCampaignYear } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import DashboardSubheader from '../../../components/dashboard-sub-header';
import { RecommendationsDashboardCards } from '../../../components/recommendations-tab-cards';
import GrowersList from '../../components/growers-list';

export const RecommendationsDashboard = ({
    rvUserNodeQuery,
}: {
    rvUserNodeQuery: ReturnType<typeof useGetUserNode>;
}) => {
    const currentCampaignYear = useCurrentCampaignYear();
    const form = useForm<{ year: number }>({
        defaultValues: {
            year: currentCampaignYear ?? new Date().getFullYear(),
        },
    });

    useEffect(() => {
        if (currentCampaignYear !== null) {
            form.setValue('year', currentCampaignYear);
        }
    }, [currentCampaignYear, form]);

    const year = form.watch('year');
    const userNode = rvUserNodeQuery.data;

    const { data: hybridRecommendations, isValidating: isValidatingHybrid } = useRecommendationsDashboard({
        year,
        type_in: ['corn', 'soy', 'density'],
        node: userNode?.id,
        ...getNodeScopeFilters('growers'),
    });
    const { data: cropProtectionRecommendations, isValidating: isValidatingCropProtection } =
        useRecommendationsDashboard({
            year,
            type_in: ['disease', 'plague', 'weed', 'seed_treatment'],
            node: userNode?.id,
            ...getNodeScopeFilters('growers'),
        });

    const isValidating = isValidatingHybrid || isValidatingCropProtection;
    const totalCount = (hybridRecommendations?.count ?? 0) + (cropProtectionRecommendations?.count ?? 0);

    return (
        <>
            <Stack direction='column' spacing={3}>
                <DashboardSubheader
                    title={_('Recomendaciones')}
                    subtitle={_('Consultá las recomendaciones generadas para cada lote durante la campaña.')}
                    form={form}
                />
                <RecommendationsDashboardCards
                    hybridRecommendations={hybridRecommendations}
                    cropProtectionRecommendations={cropProtectionRecommendations}
                    isValidating={isValidating}
                />
            </Stack>
            {userNode && (
                <GrowersList
                    userNodeId={userNode.id}
                    tab='recommendations'
                    year={year}
                    orderBy='recommendations'
                    order='desc'
                    forceEmptyState={totalCount === 0}
                />
            )}
        </>
    );
};
